import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { LOCALES, DEFAULT_LOCALE } from '@/i18n'
import { pageRoutes } from './routes'
import { setupGuards } from './guards'
import type { Locale } from '@/types'

function generateRoutes(): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = []

  for (const locale of LOCALES) {
    const children: RouteRecordRaw[] = pageRoutes.map((page) => {
      const path = page.paths[locale as Locale]
      return {
        path: path || '',
        name: `${page.name}-${locale}`,
        component: page.component,
        meta: { pageName: page.name },
      }
    })

    routes.push({
      path: `/${locale}`,
      component: () => import('@/layouts/DefaultLayout.vue'),
      children,
      meta: { locale },
    })
  }

  routes.push({
    path: '/',
    redirect: `/${DEFAULT_LOCALE}`,
  })

  // Redirect old URLs without locale prefix to default locale (fr)
  // Handles both static (/domaine) and dynamic (/gites/le-laurier) routes
  for (const page of pageRoutes) {
    const frPath = page.paths[DEFAULT_LOCALE as Locale]
    if (frPath) {
      if (frPath.includes(':')) {
        // Dynamic routes: preserve params via function redirect
        routes.push({
          path: `/${frPath}`,
          redirect: (to) => `/${DEFAULT_LOCALE}${to.path}`,
        })
      } else {
        routes.push({
          path: `/${frPath}`,
          redirect: `/${DEFAULT_LOCALE}/${frPath}`,
        })
      }
    }
  }

  routes.push({
    path: '/:pathMatch(.*)*',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'error',
        component: () => import('@/pages/ErrorPage.vue'),
      },
    ],
  })

  return routes
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: generateRoutes(),
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition || { top: 0 }
  },
})

setupGuards(router)

export default router
